import { UserPlus2 } from 'lucide-react'
import { useState } from 'react'
import { generatePath, Link } from 'react-router-dom'

import { Pagination } from '@/frontend/components/pagination'
import { SearchInput } from '@/frontend/components/search-input'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { Label } from '@/frontend/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'
import { CreateMemberForm } from '@/frontend/features/members/create-member.form'
import { useListMembers } from '@/frontend/hooks/api/use-list-members'
import { useDebounce } from '@/frontend/hooks/use-debounce'
import { useDocumentTitle } from '@/frontend/hooks/use-document-title'
import { cn } from '@/frontend/lib/utils'
import { routes } from '@/frontend/routes'

const PAGE_SIZE = 20

export function MembersPage() {
  useDocumentTitle('Members')
  const [search, setSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [page, setPage] = useState(1)
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false)
  const debouncedSearch = useDebounce(search, 300)
  const q = debouncedSearch.length >= 2 ? debouncedSearch : undefined
  const offset = (page - 1) * PAGE_SIZE
  const { data, isLoading, error } = useListMembers({
    q,
    limit: PAGE_SIZE,
    offset,
    includeArchived: showArchived,
  })

  const members = data?.members
  const totalPages = data ? Math.ceil(data.totalCount / PAGE_SIZE) : 0

  /** Reset to page 1 when the search query changes. */
  function handleSearchChange(value: string) {
    setSearch(value)
    setPage(1)
  }

  /** Reset to page 1 when the archived filter is toggled. */
  function handleShowArchivedChange(checked: boolean) {
    setShowArchived(checked)
    setPage(1)
  }

  if (error) return <div>Error: {error.message}</div>

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-headline-lg">Members</h1>
        <Button size="lg" onClick={() => setAddMemberDialogOpen(true)}>
          <UserPlus2 />
          Add Member
        </Button>
      </div>
      <div className="mb-4 flex items-center gap-4">
        <SearchInput
          value={search}
          onChange={handleSearchChange}
          placeholder="Search by name, email or phone..."
        />
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-archived"
            checked={showArchived}
            onCheckedChange={(checked) => handleShowArchivedChange(checked === true)}
          />
          <Label htmlFor="show-archived" className="font-normal cursor-pointer">
            Show archived
          </Label>
        </div>
      </div>
      <Dialog open={addMemberDialogOpen} onOpenChange={setAddMemberDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Add Member</DialogTitle>
          </DialogHeader>
          <CreateMemberForm
            onSuccess={() => setAddMemberDialogOpen(false)}
            onCancel={() => setAddMemberDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
      {isLoading ? (
        <div>Loading...</div>
      ) : members?.length === 0 ? (
        <p className="text-muted-foreground">{q ? 'No members found' : 'No members yet'}</p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Pets</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members?.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Link
                        to={generatePath(routes.member, { memberId: member.id })}
                        className={cn(
                          'hover:underline',
                          member.archivedAt && 'text-muted-foreground'
                        )}
                      >
                        {member.firstName} {member.lastName}
                      </Link>
                      {member.archivedAt && (
                        <Badge variant="secondary" size="sm">
                          Archived
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>{member.phone}</TableCell>
                  <TableCell>{member.pets.map((p) => p.name).join(', ')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}
    </div>
  )
}
